// The Activities dashboard's data for an area made on the catalogue 1.0 maps: every row of the job the map page ran
// (agg_type activities) and of its distributions (agg_type activity_distributions), read in full from the data API
// and turned into one selection result (utils/catalogueActivities.ts). A model-grid area is asked for once per cell
// size, as the map page asks. An hourly result also asks for the day, for the day's arrivals per group; a daily grid
// result also asks for the hours, for the busiest time.
//
// The previews stop at 100 rows and one area's day already has several hundred distribution rows, so only full
// exports will do, read through the CSV route (services/fullResults.ts). While that route does not exist
// (telcofy-demo#110) this rejects with a FullResultsError and the dashboard says so.
import type { CountryCode } from '../config/countries';
import type { ActivityMeasure, Granularity } from '../config/products';
import { isModelGrid } from './catalogueRequests';
import { activityRequestBodies } from './activitiesApi';
import type { ActivitiesJobParams } from './activitiesApi';
import { runFullDataJob } from './fullResults';
import { catalogueEeResponse } from '../utils/catalogueActivities';
import type { EeJobResult } from '../utils/eeActivities';

/** True for the area types whose results the dashboard can build: the catalogue's admin levels and model grid. */
export function hasActivitiesDashboard(geoType: string | null | undefined): boolean {
  return isModelGrid(geoType) || /^admin_lvl_\d+$/.test(geoType ?? '');
}

/** The job the map page ran, as the dashboard is handed it: its request as sent, and the delivery day it was sent for. */
export interface CatalogueDashboardJob {
  params: ActivitiesJobParams;
  day: string;
}

/**
 * The dashboard's job for the job on screen, built the way the map page builds its request: the job's own areas,
 * day, resolution, window and measure. null when the area type has no dashboard or the job's window is not known.
 */
export function dashboardJobOf(job: {
  ids: readonly string[];
  geoType: string | null;
  countryCode: CountryCode | null;
  day: string | null;
  granularity: Granularity | null;
  windowUtc: { start: string; end: string } | null;
  measure: ActivityMeasure | null;
}): CatalogueDashboardJob | null {
  const { ids, geoType, countryCode, day, granularity, windowUtc, measure } = job;
  if (!hasActivitiesDashboard(geoType) || !geoType || !countryCode || !day || !granularity || !windowUtc || !measure || ids.length === 0) return null;
  return {
    day,
    params: {
      agg_type: 'activities',
      measure,
      start_time: windowUtc.start,
      end_time: windowUtc.end,
      activity_type: granularity,
      geo_type: geoType,
      geo_ids: [...ids],
      country_code: countryCode,
      full: false,
    },
  };
}

/**
 * A request body for every column: the dashboard shows all the measures, so it names none. Named, `activities`
 * returns that one column only and `activity_distributions` refuses it (its columns are people and arrivals).
 */
function everyColumn(body: ActivitiesJobParams, aggType: string): Record<string, unknown> {
  const out: Record<string, unknown> = { ...body, agg_type: aggType };
  delete out.measure;
  return out;
}

/** Every row of one kind of answer, over the per-size requests of a grid area. */
async function allRows(params: ActivitiesJobParams, aggType: string, signal?: AbortSignal): Promise<unknown[]> {
  const bodies = activityRequestBodies({ ...params, full: true });
  const outs = await Promise.all(bodies.map((body) => runFullDataJob(everyColumn(body, aggType), { signal })));
  // Distribution rows no longer name their geo type (telcofy-web-backend#116), and each job asks for one, so a row
  // that leaves it out takes its request's: a grid row needs it to find its saved cell key.
  return outs.flatMap((o, i) => o.rows.map((row) => ({ ...row, geo_type: row.geo_type ?? bodies[i].geo_type })));
}

/**
 * The selection result of a submitted Activities job: `params` as the map page sent them, `day` the delivery day
 * they were sent for. Rejects with what runFullDataJob rejects with.
 */
export async function loadActivitiesDashboard(
  params: ActivitiesJobParams,
  day: string,
  o: { signal?: AbortSignal } = {},
): Promise<EeJobResult> {
  const type = params.activity_type;
  const daily: ActivitiesJobParams = { ...params, activity_type: 'daily' };
  const hourly: ActivitiesJobParams = { ...params, activity_type: 'hourly' };
  // The jobs run side by side; when one fails, or the caller gives up, this stops waiting for the others.
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (o.signal?.aborted) controller.abort();
  else o.signal?.addEventListener('abort', onAbort, { once: true });
  const { signal } = controller;
  let answers: [unknown[], unknown[], unknown[] | null, unknown[] | null, unknown[] | null];
  try {
    answers = await Promise.all([
      allRows(params, 'activities', signal),
      allRows(params, 'activity_distributions', signal),
      type === 'hourly' ? allRows(daily, 'activities', signal) : Promise.resolve(null),
      type === 'hourly' ? allRows(daily, 'activity_distributions', signal) : Promise.resolve(null),
      type === 'daily' && isModelGrid(params.geo_type) ? allRows(hourly, 'activities', signal) : Promise.resolve(null),
    ]);
  } catch (e) {
    controller.abort();
    throw e;
  } finally {
    o.signal?.removeEventListener('abort', onAbort);
  }
  const [activities, distributions, dayActivities, dayDistributions, hourlyActivities] = answers;
  const response = catalogueEeResponse({
    geoType: params.geo_type,
    date: day,
    type,
    activities,
    distributions,
    ...(dayActivities && dayDistributions ? { day: { activities: dayActivities, distributions: dayDistributions } } : {}),
    ...(hourlyActivities ? { hourlyActivities } : {}),
  });
  const requestedIds = params.geo_ids.map(String);
  return { ...response, requestedIds, areaCount: requestedIds.length, addedCells: 0 };
}
